import api, {
  buildCompanyScopeHeaders,
} from "./api";

export const DEFAULT_HR_EMPLOYEE_PAGE_SIZE = 20;

export const HR_EMPLOYEE_STATUSES = Object.freeze([
  "draft",
  "preboarding",
  "active",
  "inactive",
  "offboarding",
  "archived",
]);

export const HR_EMPLOYMENT_STATES = Object.freeze([
  "future",
  "current",
  "past",
]);

export const HR_CONTRACT_TYPES = Object.freeze([
  "CDI",
  "CDD",
  "APPRENTICESHIP",
  "PROFESSIONALIZATION",
  "INTERNSHIP",
  "TEMPORARY",
  "OTHER",
]);

function normalizeOptionalString(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}

function normalizePositiveInteger(
  value,
  fallback,
) {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
}

function appendOptionalString(
  params,
  key,
  value,
) {
  const normalized = normalizeOptionalString(value);

  if (normalized) {
    params[key] = normalized;
  }
}

export function buildEmployeeDirectoryParams({
  search = "",
  employeeStatus = "",
  contractType = "",
  employmentState = "",
  userLinked,
  establishmentUuid = "",
  departmentUuid = "",
  positionUuid = "",
  managerEmployeeUuid = "",
  page = 1,
  pageSize = DEFAULT_HR_EMPLOYEE_PAGE_SIZE,
  sortBy = "",
  sortOrder = "",
} = {}) {
  const params = {
    page: normalizePositiveInteger(page, 1),
    page_size: normalizePositiveInteger(
      pageSize,
      DEFAULT_HR_EMPLOYEE_PAGE_SIZE,
    ),
  };

  appendOptionalString(
    params,
    "search",
    search,
  );

  appendOptionalString(
    params,
    "employee_status",
    employeeStatus,
  );

  appendOptionalString(
    params,
    "contract_type",
    contractType,
  );

  appendOptionalString(
    params,
    "employment_state",
    employmentState,
  );

  appendOptionalString(
    params,
    "establishment_uuid",
    establishmentUuid,
  );

  appendOptionalString(
    params,
    "department_uuid",
    departmentUuid,
  );

  appendOptionalString(
    params,
    "position_uuid",
    positionUuid,
  );

  appendOptionalString(
    params,
    "manager_employee_uuid",
    managerEmployeeUuid,
  );

  appendOptionalString(
    params,
    "sort_by",
    sortBy,
  );

  appendOptionalString(
    params,
    "sort_order",
    sortOrder,
  );

  if (typeof userLinked === "boolean") {
    params.user_linked = userLinked;
  }

  return params;
}

export async function listEmployees({
  companyUuid,
  ...query
} = {}) {
  const response = await api.get(
    "/hr/employees",
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
      params: buildEmployeeDirectoryParams(
        query,
      ),
    },
  );

  return response.data;
}

export function buildEmployeeDetailParams({
  asOf = "",
} = {}) {
  const params = {};

  appendOptionalString(
    params,
    "as_of",
    asOf,
  );

  return params;
}


export async function getEmployeeDetail({
  employeeUuid,
  companyUuid,
  asOf = "",
} = {}) {
  const normalizedEmployeeUuid =
    normalizeOptionalString(employeeUuid);

  if (!normalizedEmployeeUuid) {
    throw new TypeError(
      "employeeUuid is required.",
    );
  }

  const response = await api.get(
    `/hr/employees/${encodeURIComponent(
      normalizedEmployeeUuid,
    )}`,
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
      params: buildEmployeeDetailParams({
        asOf,
      }),
    },
  );

  return response.data;
}

// ============================================================
// EMPLOYEE ADMIN MUTATION API
// LOT 2.6.6.7
// ============================================================

function requireEmployeeUuid(employeeUuid) {
  const normalized =
    normalizeOptionalString(employeeUuid);

  if (!normalized) {
    throw new TypeError(
      "employeeUuid is required.",
    );
  }

  return normalized;
}

function employeeResourcePath(employeeUuid) {
  const normalized =
    requireEmployeeUuid(employeeUuid);

  return (
    `/hr/employees/` +
    `${encodeURIComponent(normalized)}`
  );
}

export async function updateEmployeeIdentity({
  employeeUuid,
  employeeNumber,
  firstName,
  lastName,
} = {}) {
  const payload = {};

  if (employeeNumber !== undefined) {
    payload.employee_number = employeeNumber;
  }

  if (firstName !== undefined) {
    payload.first_name = firstName;
  }

  if (lastName !== undefined) {
    payload.last_name = lastName;
  }

  const response = await api.patch(
    employeeResourcePath(employeeUuid),
    payload,
  );

  return response.data;
}

async function runEmployeeLifecycleAction({
  employeeUuid,
  action,
  reason,
}) {
  const payload = {};

  if (reason !== undefined) {
    payload.reason = reason;
  }

  const response = await api.post(
    `${employeeResourcePath(employeeUuid)}` +
      `/lifecycle/${action}`,
    payload,
  );

  return response.data;
}

export function startEmployeePreboarding({
  employeeUuid,
  reason,
} = {}) {
  return runEmployeeLifecycleAction({
    employeeUuid,
    reason,
    action: "start-preboarding",
  });
}

export function activateEmployee({
  employeeUuid,
  reason,
} = {}) {
  return runEmployeeLifecycleAction({
    employeeUuid,
    reason,
    action: "activate",
  });
}

export function beginEmployeeOffboarding({
  employeeUuid,
  reason,
} = {}) {
  return runEmployeeLifecycleAction({
    employeeUuid,
    reason,
    action: "begin-offboarding",
  });
}

export function deactivateEmployee({
  employeeUuid,
  reason,
} = {}) {
  return runEmployeeLifecycleAction({
    employeeUuid,
    reason,
    action: "deactivate",
  });
}

export function archiveEmployee({
  employeeUuid,
  reason,
} = {}) {
  return runEmployeeLifecycleAction({
    employeeUuid,
    reason,
    action: "archive",
  });
}

export async function attachEmployeeUser({
  employeeUuid,
  userUuid,
} = {}) {
  const normalizedUserUuid =
    normalizeOptionalString(userUuid);

  if (!normalizedUserUuid) {
    throw new TypeError(
      "userUuid is required.",
    );
  }

  const response = await api.put(
    `${employeeResourcePath(employeeUuid)}/user`,
    {
      user_uuid: normalizedUserUuid,
    },
  );

  return response.data;
}

export async function detachEmployeeUser({
  employeeUuid,
} = {}) {
  const response = await api.delete(
    `${employeeResourcePath(employeeUuid)}/user`,
  );

  return response.data;
}

